import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { CheckCircle2, Image as ImageIcon, Search, XCircle } from 'lucide-react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useSocket } from '../hooks/useSocket'
import { useToast } from '../contexts/ToastContext'

const PAGE_SIZE = 20

type KycStatus = 'all' | 'pending' | 'under_review' | 'approved' | 'rejected'

type KycRow = {
  agent_id: string
  agent_name?: string | null
  phone?: string | null
  kyc_status?: string | null
  verification_status?: string | null
  submitted_at?: string | null
  document_types?: string | null
}

type KycDoc = {
  id: string
  doc_type: string
  review_status: string
  review_notes?: string | null
  signed_get_url?: string
}

const normalizeStatus = (row: KycRow): KycStatus => {
  const raw = String(row.verification_status || row.kyc_status || 'pending').toLowerCase()
  if (raw === 'approved' || raw === 'verified') return 'approved'
  if (raw === 'rejected') return 'rejected'
  if (raw === 'under_review') return 'under_review'
  return 'pending'
}

export default function KycApprovalPage() {
  const colors = useColors()
  const { addToast } = useToast()

  const [rows, setRows] = useState<KycRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<KycStatus>('all')
  const [sortBy, setSortBy] = useState<'name' | 'submitted_at' | 'status'>('submitted_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null)
  const [docsByAgent, setDocsByAgent] = useState<Record<string, KycDoc[]>>({})
  const [docsLoadingAgent, setDocsLoadingAgent] = useState<string | null>(null)

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [docActionLoadingIds, setDocActionLoadingIds] = useState<string[]>([])
  const [docRejectReasons, setDocRejectReasons] = useState<Record<string, string>>({})

  const [newSubmissionBadge, setNewSubmissionBadge] = useState(0)

  const [stats, setStats] = useState({
    pending: 0,
    underReview: 0,
    approvedToday: 0,
    rejectedToday: 0,
  })

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [queueResponse, pendingRes, reviewRes, approvedRes, rejectedRes] = await Promise.all([
        adminApi.getAgentKycQueue({ status: 'all', page: 1, limit: 300 }),
        adminApi.getAgentKycQueue({ status: 'pending', page: 1, limit: 1 }),
        adminApi.getAgentKycQueue({ status: 'under_review', page: 1, limit: 1 }),
        adminApi.getAgentKycQueue({ status: 'approved', page: 1, limit: 300 }),
        adminApi.getAgentKycQueue({ status: 'rejected', page: 1, limit: 300 }),
      ])

      const queueData = queueResponse?.data?.data || queueResponse?.data || []
      const pendingCount = Number(pendingRes?.data?.pagination?.total || 0)
      const reviewCount = Number(reviewRes?.data?.pagination?.total || 0)

      const today = new Date().toDateString()
      const approvedRows = approvedRes?.data?.data || []
      const rejectedRows = rejectedRes?.data?.data || []

      const approvedToday = approvedRows.filter((r: KycRow) => r.submitted_at && new Date(r.submitted_at).toDateString() === today).length
      const rejectedToday = rejectedRows.filter((r: KycRow) => r.submitted_at && new Date(r.submitted_at).toDateString() === today).length

      setRows(Array.isArray(queueData) ? queueData : [])
      setStats({ pending: pendingCount, underReview: reviewCount, approvedToday, rejectedToday })
    } catch {
      setError('Unable to load KYC queue.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchQueue()
  }, [fetchQueue])

  useSocket({
    'agent:kyc_doc_uploaded': () => {
      setNewSubmissionBadge((prev) => prev + 1)
      addToast('New KYC submission received', 'info')
      void fetchQueue()
    },
  })

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase()

    const next = rows.filter((row) => {
      const status = normalizeStatus(row)
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (!needle) return true
      return (
        String(row.agent_name || '').toLowerCase().includes(needle)
        || String(row.phone || '').toLowerCase().includes(needle)
        || String(row.document_types || '').toLowerCase().includes(needle)
      )
    })

    next.sort((a, b) => {
      if (sortBy === 'name') {
        const compare = String(a.agent_name || '').localeCompare(String(b.agent_name || ''))
        return sortOrder === 'asc' ? compare : -compare
      }
      if (sortBy === 'status') {
        const compare = normalizeStatus(a).localeCompare(normalizeStatus(b))
        return sortOrder === 'asc' ? compare : -compare
      }
      const left = new Date(a.submitted_at || 0).getTime()
      const right = new Date(b.submitted_at || 0).getTime()
      return sortOrder === 'asc' ? left - right : right - left
    })

    return next
  }, [rows, search, sortBy, sortOrder, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const toggleExpand = async (agentId: string) => {
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null)
      return
    }

    setExpandedAgentId(agentId)
    if (docsByAgent[agentId]) return

    try {
      setDocsLoadingAgent(agentId)
      const response = await adminApi.getAgentKycDocuments(agentId)
      const docs = response?.data || []
      setDocsByAgent((prev) => ({ ...prev, [agentId]: Array.isArray(docs) ? docs : [] }))
    } catch {
      addToast('Failed to load documents', 'error')
      setDocsByAgent((prev) => ({ ...prev, [agentId]: [] }))
    } finally {
      setDocsLoadingAgent(null)
    }
  }

  const refreshDocs = async (agentId: string) => {
    const response = await adminApi.getAgentKycDocuments(agentId)
    const docs = response?.data || []
    setDocsByAgent((prev) => ({ ...prev, [agentId]: Array.isArray(docs) ? docs : [] }))
  }

  const reviewDoc = async (agentId: string, doc: KycDoc, status: 'approved' | 'rejected') => {
    const reason = (docRejectReasons[doc.id] || '').trim()
    if (status === 'rejected' && reason.length < 10) {
      addToast('Reject reason must be at least 10 characters', 'error')
      return
    }

    try {
      setDocActionLoadingIds((prev) => [...prev, doc.id])
      await adminApi.reviewAgentKycDocument(agentId, doc.id, {
        review_status: status,
        review_notes: status === 'rejected' ? reason : undefined,
      })
      addToast(status === 'approved' ? 'Document approved' : 'Document rejected. Agent can re-upload it.', status === 'approved' ? 'success' : 'info')
      await refreshDocs(agentId)
      await fetchQueue()
    } catch {
      addToast('Document review failed', 'error')
    } finally {
      setDocActionLoadingIds((prev) => prev.filter((id) => id !== doc.id))
    }
  }

  const isAllSelectedOnPage = pagedRows.length > 0 && pagedRows.every((r) => selectedIds.includes(r.agent_id))

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>KYC Approval</h2>
          <p className="font-body" style={{ margin: '4px 0 0', color: colors.textMuted }}>Review and approve agent KYC documents</p>
        </div>
        {newSubmissionBadge > 0 ? (
          <button
            type="button"
            title="Click to refresh and dismiss"
            onClick={() => { setNewSubmissionBadge(0); void fetchQueue() }}
            style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            {newSubmissionBadge} new submission{newSubmissionBadge > 1 ? 's' : ''} · refresh
          </button>
        ) : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total pending', value: stats.pending },
          { label: 'Under review', value: stats.underReview },
          { label: 'Approved today', value: stats.approvedToday },
          { label: 'Rejected today', value: stats.rejectedToday },
        ].map((card) => (
          <div key={card.label} style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
            <div style={{ color: colors.textMuted, fontSize: 12 }}>{card.label}</div>
            <div style={{ color: colors.textPrimary, fontSize: 22, fontWeight: 700 }}>{card.value}</div>
          </div>
        ))}
      </div>

      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ position: 'relative', minWidth: 280 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: colors.textMuted }} />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder="Search by name, phone, document"
              style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: `1px solid ${colors.border}` }}
            />
          </div>

          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as KycStatus); setPage(1) }} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'name' | 'submitted_at' | 'status')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
            <option value="submitted_at">Sort: Submitted Date</option>
            <option value="name">Sort: Agent Name</option>
            <option value="status">Sort: Status</option>
          </select>

          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}` }}>
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>

          <button onClick={() => void fetchQueue()} style={{ marginLeft: 'auto', padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgPrimary }}>
            Refresh
          </button>
        </div>

        {selectedIds.length > 0 ? (
          <div style={{ marginBottom: 10, color: colors.textMuted, fontSize: 13 }}>
            {selectedIds.length} selected. Expand each row to review documents individually.
          </div>
        ) : null}

        {loading ? <div style={{ padding: 24, color: colors.textMuted }}>Loading KYC queue...</div> : error ? (
          <div style={{ padding: 24, color: '#991B1B' }}>{error}</div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: 24, color: colors.textMuted }}>No KYC submissions found.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.bgSecondary }}>
                    <th style={{ textAlign: 'left', padding: 10 }}>
                      <input
                        type="checkbox"
                        checked={isAllSelectedOnPage}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds((prev) => [...new Set([...prev, ...pagedRows.map((r) => r.agent_id)])])
                          } else {
                            setSelectedIds((prev) => prev.filter((id) => !pagedRows.some((r) => r.agent_id === id)))
                          }
                        }}
                      />
                    </th>
                    {['Agent Name', 'Phone', 'Submitted Date', 'Document Type', 'Status', 'Actions'].map((head) => (
                      <th key={head} style={{ textAlign: 'left', padding: 10, fontSize: 12, color: colors.textMuted }}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => {
                    const status = normalizeStatus(row)
                    return (
                      <Fragment key={row.agent_id}>
                        <tr style={{ borderTop: `1px solid ${colors.border}` }}>
                          <td style={{ padding: 10 }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row.agent_id)}
                              onChange={(e) => {
                                setSelectedIds((prev) => e.target.checked ? [...prev, row.agent_id] : prev.filter((id) => id !== row.agent_id))
                              }}
                            />
                          </td>
                          <td style={{ padding: 10, cursor: 'pointer' }} onClick={() => void toggleExpand(row.agent_id)}>{row.agent_name || 'Unnamed agent'}</td>
                          <td style={{ padding: 10 }}>{row.phone || '-'}</td>
                          <td style={{ padding: 10 }}>{row.submitted_at ? format(new Date(row.submitted_at), 'dd MMM yyyy, hh:mm a') : '-'}</td>
                          <td style={{ padding: 10 }}>{row.document_types || '-'}</td>
                          <td style={{ padding: 10 }}>
                            <span style={{
                              background: status === 'approved' ? '#DCFCE7' : status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                              color: status === 'approved' ? '#166534' : status === 'rejected' ? '#991B1B' : '#92400E',
                              padding: '4px 8px',
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 600,
                            }}>
                              {status.replace('_', ' ')}
                            </span>
                          </td>
                          <td style={{ padding: 10 }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button onClick={() => void toggleExpand(row.agent_id)} style={{ background: '#15803D', color: '#fff', border: 0, borderRadius: 6, padding: '6px 8px' }}>
                                Review documents
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedAgentId === row.agent_id ? (
                          <tr style={{ borderTop: `1px solid ${colors.border}`, background: colors.bgSecondary }}>
                            <td colSpan={7} style={{ padding: 12 }}>
                              {docsLoadingAgent === row.agent_id ? (
                                <div style={{ color: colors.textMuted }}>Loading documents...</div>
                              ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                                  {(docsByAgent[row.agent_id] || []).map((doc) => {
                                    const busy = docActionLoadingIds.includes(doc.id)
                                    const reason = docRejectReasons[doc.id] || ''
                                    return (
                                    <div key={doc.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', background: colors.bgCard }}>
                                      {doc.signed_get_url ? (
                                        <a href={doc.signed_get_url} target="_blank" rel="noreferrer">
                                          <img src={doc.signed_get_url} alt={doc.doc_type} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                                        </a>
                                      ) : (
                                        <div style={{ height: 130, display: 'grid', placeItems: 'center', color: colors.textMuted }}><ImageIcon size={20} /></div>
                                      )}
                                      <div style={{ padding: 8, display: 'grid', gap: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                          <span style={{ fontSize: 12, color: colors.textPrimary }}>{doc.doc_type}</span>
                                          <span style={{ fontSize: 11, color: doc.review_status === 'approved' ? '#166534' : doc.review_status === 'rejected' ? '#991B1B' : '#92400E', textTransform: 'capitalize' }}>
                                            {doc.review_status}
                                          </span>
                                        </div>
                                        {doc.review_status === 'rejected' && doc.review_notes ? (
                                          <div style={{ fontSize: 12, color: '#991B1B' }}>{doc.review_notes}</div>
                                        ) : null}
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button disabled={busy || doc.review_status === 'approved'} onClick={() => void reviewDoc(row.agent_id, doc, 'approved')} style={{ flex: 1, background: '#15803D', color: '#fff', border: 0, borderRadius: 6, padding: '6px 8px', opacity: busy || doc.review_status === 'approved' ? 0.6 : 1 }}>
                                            <CheckCircle2 size={14} /> Approve
                                          </button>
                                          <button disabled={busy} onClick={() => void reviewDoc(row.agent_id, doc, 'rejected')} style={{ flex: 1, background: '#B91C1C', color: '#fff', border: 0, borderRadius: 6, padding: '6px 8px', opacity: busy ? 0.6 : 1 }}>
                                            <XCircle size={14} /> Reject
                                          </button>
                                        </div>
                                        <textarea
                                          value={reason}
                                          onChange={(e) => setDocRejectReasons((prev) => ({ ...prev, [doc.id]: e.target.value }))}
                                          rows={2}
                                          placeholder="Reject reason for this document"
                                          style={{ width: '100%', border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, fontSize: 12 }}
                                        />
                                      </div>
                                    </div>
                                  )})}
                                  {(docsByAgent[row.agent_id] || []).length === 0 ? <div style={{ color: colors.textMuted }}>No uploaded docs</div> : null}
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: colors.textMuted, fontSize: 12 }}>
                Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} style={{ padding: '6px 10px' }}>Prev</button>
                <button disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} style={{ padding: '6px 10px' }}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

    </div>
  )
}
